import { FComponent } from "../f-component";
import HighchartsModern from "@/utils/highcharts-modern";
import { declOfNum } from "@/utils/string/decl-of-num";
import { merge } from "lodash";

let uniqueId = 1;

export class ViewModel extends FComponent {
  constructor(params, element) {
    super(params, element);

    this.id = uniqueId++;
    this.chartContainerId = `heatmap-chart-${this.id}`;

    // Observables for component state
    this.loading = ko.observable(false);
    this.error = ko.observable(null);
    this.chartInitialized = ko.observable(false);
    this.options = params.options || {};

    this.areaCheck = params.areaCheck
    this.clickAreas = ko.isObservable(params.clickAreas)
      ? params.clickAreas
      : ko.observableArray(params.clickAreas || []);
    this.enableAreas = ko.isObservable(params.enableAreas)
      ? params.enableAreas
      : ko.observable(params.enableAreas !== false);
    this.originalImageDimensions = ko.isObservable(params.originalImageDimensions)
      ? params.originalImageDimensions
      : ko.observable(params.originalImageDimensions || { width: 0, height: 0 });

    this.addSubscription(
      this.originalImageDimensions.subscribe(() => {
        this.recalculateLayout();
      })
    );
    this.addSubscription(
      this.clickAreas.subscribe(() => {
        this.recalculateLayout();
      })
    );
    this.addSubscription(
      this.enableAreas.subscribe(() => {
        this.recalculateLayout();
      })
    );

    // Parameters from parent
    this.imageSrc = ko.isObservable(params.imageSrc)
      ? params.imageSrc
      : ko.observable(params.imageSrc);
    this.data = ko.isObservable(params.data) ? params.data : ko.observable(params.data || []);
    this.gridWidth = ko.isObservable(this.options.gridWidth) ? this.options.gridWidth : ko.observable(this.options.gridWidth);
    this.gridHeight = ko.isObservable(this.options.gridHeight) ? this.options.gridHeight : ko.observable(this.options.gridHeight);

    this.onPointClick = params.onPointClick || (() => {});
    this._onImageLoad = params.onImageLoad || null;

    // Chart instance
    this.chart = null;
    this.resizeObserver = null;
    this.currentDimensions = ko.observable({ width: 0, height: 0 });
    this.imageOffset = ko.observable({ top: 0, left: 0 });
    this.debouncedRecalculateLayout = this.debounce(this.recalculateLayout, 50);

    // Image loading state
    this.imageLoaded = ko.observable(false);
    this.imageError = ko.observable(false);

    this.hasData = ko.computed(() => {
      const data = this.data();
      return data && data.length > 0;
    });

    this.addSubscription(
      this.data.subscribe((newData) => {
        if (this.chart && this.chartInitialized()) {
          this.updateChartData(newData);
        } else if (!this.chart && newData && newData.length > 0 && this.imageLoaded()) {
          console.log("[HEATMAP CHART] Creating chart due to new data");
          
          const imageElement = this.element.querySelector(".heatmap-chart__background-image");
          if (imageElement) {
            const containerWidth = imageElement.offsetWidth;
            const containerHeight = imageElement.offsetHeight;
            this.currentDimensions({ width: containerWidth, height: containerHeight });
            this.createChart(containerWidth, containerHeight);
          }
        } else if (!newData || newData.length === 0) {
          console.log("[HEATMAP CHART] Data became empty, hiding chart");
          this.chartInitialized(false);
          if (this.chart) {
            this.chart.destroy();
            this.chart = null;
          }
        }
      })
    );

    this.addSubscription(
      this.imageSrc.subscribe((newSrc) => {
        if (newSrc) {
          this.imageLoaded(false);
          this.imageError(false);
          this.chartInitialized(false);
        }
      })
    );
  }

  calculatePixelX(proportionalX) {
    const displayed = this.currentDimensions();
    return (proportionalX || 0) * displayed.width;
  }

  calculatePixelY(proportionalY) {
    const displayed = this.currentDimensions();
    return (proportionalY || 0) * displayed.height;
  }

  calculatePixelWidth(proportionalWidth) {
    const displayed = this.currentDimensions();
    return (proportionalWidth || 0) * displayed.width;
  }

  calculatePixelHeight(proportionalHeight) {
    const displayed = this.currentDimensions();
    return (proportionalHeight || 0) * displayed.height;
  }

  getAreaNameStyles(area) {
    const areaX = this.calculatePixelX(ko.unwrap(area.x()));
    const areaY = this.calculatePixelY(ko.unwrap(area.y()));
    const areaWidth = this.calculatePixelWidth(ko.unwrap(area.width()));
    const areaHeight = this.calculatePixelHeight(ko.unwrap(area.height()));
    const imageOffset = this.imageOffset();
    const displayedDimensions = this.currentDimensions();

    const labelHeight = 25; // Fixed height of the tooltip
    const labelWidth = 257; // Fixed width of the tooltip
    const labelPadding = 5; // Padding between area and tooltip
    const labelOffset = 3; // Small offset for alignment
    const edgeShift = 5; // Shift from edge

    // Image boundaries relative to the container
    const imageLeftBound = imageOffset.x;
    const imageRightBound = imageOffset.x + displayedDimensions.width;
    const imageTopBound = imageOffset.y;
    const imageBottomBound = imageOffset.y + displayedDimensions.height;

    // Available space within image boundaries
    const availableSpaceRight = imageRightBound - (areaX + imageOffset.x + areaWidth);
    const availableSpaceLeft = areaX + imageOffset.x - imageLeftBound;
    const availableSpaceBottom = imageBottomBound - (areaY + imageOffset.y + areaHeight);
    const availableSpaceTop = areaY + imageOffset.y - imageTopBound;

    // Flags to check if tooltip fits
    const canPlaceBelow = availableSpaceBottom >= labelHeight + labelPadding;
    const canPlaceAbove = availableSpaceTop >= labelHeight + labelPadding;
    const canPlaceRight = availableSpaceRight >= labelWidth + labelPadding;
    const canPlaceLeft = availableSpaceLeft >= labelWidth + labelPadding;

    let top, bottom, left, right;

    // Helper function to clamp position within image bounds
    const clampToImageBounds = (pos, max, size) => {
      return Math.max(0, Math.min(pos, max - size));
    };

    if (canPlaceBelow) {
      // Place below the area
      top = clampToImageBounds(areaHeight + labelPadding, displayedDimensions.height - labelHeight, labelHeight) + 'px';
      bottom = 'auto';

      if (canPlaceRight) {
        left = clampToImageBounds(-labelOffset + edgeShift, displayedDimensions.width - labelWidth, labelWidth) + 'px';
        right = 'auto';
      } else if (canPlaceLeft) {
        right = clampToImageBounds(-labelOffset + edgeShift, displayedDimensions.width - labelWidth, labelWidth) + 'px';
        left = 'auto';
      } else {
        // Center horizontally, ensure it stays within image
        left = clampToImageBounds(
          areaWidth / 2 - labelWidth / 2,
          displayedDimensions.width - labelWidth,
          labelWidth
        ) + 'px';
        right = 'auto';
      }
    } else if (canPlaceAbove) {
      // Place above the area
      bottom = clampToImageBounds(areaHeight + labelPadding, displayedDimensions.height - labelHeight, labelHeight) + 'px';
      top = 'auto';

      if (canPlaceRight) {
        left = clampToImageBounds(-labelOffset + edgeShift, displayedDimensions.width - labelWidth, labelWidth) + 'px';
        right = 'auto';
      } else if (canPlaceLeft) {
        right = clampToImageBounds(-labelOffset + edgeShift, displayedDimensions.width - labelWidth, labelWidth) + 'px';
        left = 'auto';
      } else {
        left = clampToImageBounds(
          areaWidth / 2 - labelWidth / 2,
          displayedDimensions.width - labelWidth,
          labelWidth
        ) + 'px';
        right = 'auto';
      }
    } else if (canPlaceRight) {
      // Place to the right of the area
      left = clampToImageBounds(areaWidth + labelPadding, displayedDimensions.width - labelWidth, labelWidth) + 'px';
      right = 'auto';
      top = clampToImageBounds(0, displayedDimensions.height - labelHeight, labelHeight) + 'px';
      bottom = 'auto';
    } else if (canPlaceLeft) {
      // Place to the left of the area
      right = clampToImageBounds(areaWidth + labelPadding, displayedDimensions.width - labelWidth, labelWidth) + 'px';
      left = 'auto';
      top = clampToImageBounds(0, displayedDimensions.height - labelHeight, labelHeight) + 'px';
      bottom = 'auto';
    } else {
      // Fallback: Center within the area, fully constrained within image
      left = clampToImageBounds(
        areaWidth / 2 - labelWidth / 2,
        displayedDimensions.width - labelWidth,
        labelWidth
      ) + 'px';
      right = 'auto';
      top = clampToImageBounds(
        areaHeight / 2 - labelHeight / 2,
        displayedDimensions.height - labelHeight,
        labelHeight
      );
      top = top * 2 + (labelHeight + labelPadding) + 'px'
      bottom = 'auto';
    }

    return {
      top: top,
      bottom: bottom,
      left: left,
      right: right,
      maxWidth: Math.min(labelWidth, displayedDimensions.width) + 'px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    };
  }

  // Если изображение уже в кэше браузера, событие `load` у <img> может не
  // сработать → imageLoaded остаётся false → при приходе данных график не
  // создаётся (тепловая карта пустая). Проверяем готовность вручную после
  // рендера (issue 102/105).
  ensureImageLoaded() {
    if (this.imageLoaded()) return;
    const img =
      this.element &&
      this.element.querySelector(".heatmap-chart__background-image");
    if (img && img.complete && img.naturalWidth > 0) {
      this.onImageLoad(this, { target: img });
    }
  }

  onImageLoad(data, event) {
    if (this.imageLoaded()) return;
    console.log("[HEATMAP CHART] Image loaded successfully");
    const img = event.target;

    this.imageLoaded(true);
    this.imageError(false);
    this.error(null);

    setTimeout(() => {
      this.initializeChart(img);
      this.recalculateLayout();
      if (this._onImageLoad) this._onImageLoad(img);
    }, 50);
  }

  onImageError() {
    console.error("[HEATMAP CHART] Failed to load image:", this.imageSrc());
    this.imageError(true);
    this.imageLoaded(false);
    this.error("Не удалось загрузить изображение");
  }

  initializeChart(img) {
    const container = document.getElementById(this.chartContainerId);
    if (!container) {
      console.error("[HEATMAP CHART] Container not found:", this.chartContainerId);
      return;
    }

    const imageElement = this.element.querySelector(".heatmap-chart__background-image");
    if (!imageElement) {
      console.error("[HEATMAP CHART] Image element not found");
      return;
    }

    const containerWidth = imageElement.offsetWidth;
    const containerHeight = imageElement.offsetHeight;

    console.log("[HEATMAP CHART] Container dimensions:", containerWidth, "x", containerHeight);

    this.currentDimensions({ width: containerWidth, height: containerHeight });
    
    const heatmapData = this.data();
    if (heatmapData && heatmapData.length > 0) {
      this.createChart(containerWidth, containerHeight);
    } else {
      console.log("[HEATMAP CHART] No data available, skipping chart initialization");
    }
    
    this.setupResizeObserver();
  }

  createChart(width, height) {
    const heatmapData = this.data();
    console.log("heatmapData", heatmapData);
    console.log("[HEATMAP CHART] Creating chart with data points:", heatmapData.length);

    if (!heatmapData || heatmapData.length === 0) {
      console.log("[HEATMAP CHART] No data available, aborting chart creation");
      return;
    }

    console.log("[HEATMAP CHART] gridWidth", this.gridWidth());
    console.log("[HEATMAP CHART] gridHeight", this.gridHeight());
    if (!this.gridWidth() || !this.gridHeight()) {
      console.error("[HEATMAP CHART] Grid width or height is not set");
      return;
    }

    const maxValue = Math.max(1, ...heatmapData.map((d) => d.value));

    const defaultOptions = {
      chart: {
        height: "100%",
        type: "heatmap",
        backgroundColor: "transparent",
        plotBorderWidth: 0,
        margin: [0, 0, 0, 0],
      },
      plotOptions: {
        series: {
          radiusPlus: 10, // Радиус влияния точки
          blur: 5,       // Размытие для сглаживания
          pointPadding: 0 // Убираем промежутки между ячейками
        }
      },
      title: {
        text: undefined,
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      yAxis: {
        visible: false,
        title: {
          text: undefined,
        },
        endOnTick: false,
        minPadding: 0,
        maxPadding: 0,
        min: 0,
        max: ko.unwrap(this.gridHeight),
        tickInterval: 1,
        reversed: true,
      },
      xAxis: {
        visible: false,
        margin: 0,
        minPadding: 0,
        maxPadding: 0,
        min: 0,
        max: ko.unwrap(this.gridWidth),
        tickInterval: 1,
        endOnTick: false,
      },
      colorAxis: {
        stops: [
            [0, 'rgba(0, 0, 0, 0)'],
            [0.1, 'rgba(61, 0, 255, 0.2)'],
            [0.3, 'rgba(0, 255, 188, 0.3)'],
            [0.6, 'rgba(194, 255, 0, 0.6)'],
            [0.9, this.areaCheck ? 'rgba(255, 0, 67, 0.7)' : 'rgba(255, 0, 67, 0.9)']
        ]
      },
      tooltip: {
        enabled: false,
      },
      series: [
        {
          type: "heatmap",
          data: heatmapData,
          interpolation: true,
          blur: 0.2,
          borderWidth: 0,
        },
      ],
    };

    const chartOptions = merge(defaultOptions, ko.unwrap(this.options));
    this.chart = HighchartsModern.chart(this.chartContainerId, chartOptions);
    
    this.chart.setSize(width, height, false);
    this.chartInitialized(true);

    console.log("[HEATMAP CHART] Chart created successfully");
  }

  updateChartData(newData) {
    if (!this.chart || !this.chartInitialized()) return;

    console.log("[HEATMAP CHART] Updating chart with new data:", newData.length, "points");

    if (!newData || newData.length === 0) {
      console.log("[HEATMAP CHART] No data points, hiding chart");
      this.chartInitialized(false);
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
      return;
    }

    this.chart.series[0].setData(newData, true);

    const maxValue = Math.max(1, ...newData.map((d) => d.value));
    this.chart.colorAxis[0].update({ max: maxValue });
  }

  setupResizeObserver() {
    const imageElement = this.element.querySelector(".heatmap-chart__background-image");
    if (!imageElement || this.resizeObserver) return;

    this.resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        this.debouncedRecalculateLayout();
      }
    });

    this.resizeObserver.observe(imageElement);
  }

  recalculateLayout() {
    const imageElement = this.element.querySelector(".heatmap-chart__background-image");

    if (imageElement) {
      const newWidth = imageElement.offsetWidth;
      const newHeight = imageElement.offsetHeight;

      this.imageOffset({
        top: imageElement.offsetTop,
        left: imageElement.offsetLeft,
      });
      this.currentDimensions({ width: newWidth, height: newHeight });

      if (this.chart && this.chartInitialized()) {
        const currentChartWidth = this.chart.chartWidth;
        const currentChartHeight = this.chart.chartHeight;

        if (
          Math.abs(newWidth - currentChartWidth) > 1 ||
          Math.abs(newHeight - currentChartHeight) > 1
        ) {
          console.log("[HEATMAP CHART] Resizing chart to:", newWidth, "x", newHeight);
          this.chart.setSize(newWidth, newHeight, false);
        }
      }
    }
  }

  debounce(func, delay) {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), delay);
    };
  }

  dispose() {
    console.log("[HEATMAP CHART] Disposing component");

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }

    super.dispose();
  }
}
